import React from "react";
import { Box, Typography } from "@mui/material";
import NorthRoundedIcon from "@mui/icons-material/NorthRounded";
import SouthRoundedIcon from "@mui/icons-material/SouthRounded";
import { useTranslation } from "react-i18next";

import { brand } from "../../../themes/theme";
import ViewContainer from "../../../components/ViewContainer";
import DashboardGrid from "../../../components/grid/DashboardGrid";
import Panel from "../../../components/grid/Panel";
import DataTable from "../../../components/DataTable";
import StackedBar from "../../../components/charts/StackedBar";
import StatusTag from "../../../components/StatusTag";
import SubgroupFilterBar, { useSubgroupFilter } from "./SubgroupFilterBar";

import overviewData from "../../../data/pharma/subgroupsOverview.json";

const brandHex = { primary: "#3248CA", primaryLight: "#5B6FD9", teal: "#ADCAD6", blueSlate: "#3D4F5F", accentMedium: "#FF8239", accent: "#FF5F24" };

// Modulo 3.1 — Subgroups · Overview (copiado del modulo Subgroups del HTML).
function SubgroupsOverviewView() {
    const { t } = useTranslation();
    const { filtered } = useSubgroupFilter();

    const characterisationRows = filtered.map((s) => {
        const extra = overviewData.characterisation.find((c) => c.subgroupId === s.id);
        const slope = s.qolSlope != null ? s.qolSlope : s.qolSlopeObserved;
        const slopeUp = slope > 0;
        return {
            subgroup: (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.9, minWidth: 0 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: brandHex[s.color], flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: "text.primary" }}>{s.name}</Typography>
                </Box>
            ),
            n: s.n,
            share: `${s.sharePct}%`,
            persistence: (
                <Box
                    sx={{
                        display: "inline-flex",
                        px: 0.9,
                        py: 0.15,
                        borderRadius: "5px",
                        bgcolor: s.persistence12mPct >= 50 ? "rgba(50,72,202,0.08)" : brand.gray100,
                    }}
                >
                    <Typography sx={{ fontSize: 12, fontWeight: 500, color: s.persistence12mPct >= 50 ? "primary.main" : "text.secondary" }}>
                        {s.persistence12mPct}%
                    </Typography>
                </Box>
            ),
            qolSlope: s.qolSlope != null ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                    {slopeUp ? (
                        <NorthRoundedIcon sx={{ fontSize: 12, color: "primary.main" }} />
                    ) : (
                        <SouthRoundedIcon sx={{ fontSize: 12, color: "accent.main" }} />
                    )}
                    <Typography sx={{ fontSize: 12, fontWeight: 500, color: slopeUp ? "primary.main" : "accent.main" }}>
                        {Math.abs(s.qolSlope)}
                    </Typography>
                </Box>
            ) : (
                <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                    +{s.qolSlopeObserved} obs / +{s.qolSlopeReported} rep
                </Typography>
            ),
            rootCause: extra?.dominantRootCause ? <StatusTag tone="neutral">{extra.dominantRootCause}</StatusTag> : "—",
            application: extra?.application ? <StatusTag tone="info">{extra.application}</StatusTag> : "—",
        };
    });

    const definingRows = filtered.map((s) => {
        const extra = overviewData.definingVariables.find((c) => c.subgroupId === s.id);
        return { subgroup: s.name, pattern: extra?.pattern || "—" };
    });

    const shareItems = filtered.map((s) => ({ label: s.name, pct: s.sharePct, value: `${s.n} · ${s.sharePct}%`, color: brandHex[s.color] }));

    return (
        <ViewContainer title={t("views.pharmaSubgroupsOverview.title")} description={t("views.pharmaSubgroupsOverview.description")}>
            <SubgroupFilterBar />
            <DashboardGrid gap={2} rowGap={2.5}>
                <Panel span={12} dense title={t("views.pharmaSubgroupsOverview.definingVariablesTitle")} subtitle={t("views.pharmaSubgroupsOverview.definingVariablesHint")}>
                    <DataTable
                        columns={[
                            { key: "subgroup", label: t("views.pharmaSubgroupsOverview.table.subgroup"), width: "1fr" },
                            { key: "pattern", label: t("views.pharmaSubgroupsOverview.table.pattern"), width: "2.4fr" },
                        ]}
                        rows={definingRows}
                    />
                </Panel>

                <Panel span={12} dense title={t("views.pharmaSubgroupsOverview.characterisationTitle")} subtitle={t("views.pharmaSubgroupsOverview.characterisationHint")}>
                    <DataTable
                        columns={[
                            { key: "subgroup", label: t("views.pharmaSubgroupsOverview.table.subgroup"), width: "1.3fr" },
                            { key: "n", label: t("views.pharmaSubgroupsOverview.table.n"), align: "right", width: "0.5fr" },
                            { key: "share", label: t("views.pharmaSubgroupsOverview.table.share"), align: "right", width: "0.5fr" },
                            { key: "persistence", label: t("views.pharmaSubgroupsOverview.table.persistence"), align: "right", width: "0.7fr" },
                            { key: "qolSlope", label: t("views.pharmaSubgroupsOverview.table.qolSlope"), align: "right", width: "1fr" },
                            { key: "rootCause", label: t("views.pharmaSubgroupsOverview.table.rootCause"), width: "1.3fr" },
                            { key: "application", label: t("views.pharmaSubgroupsOverview.table.application"), width: "1.1fr" },
                        ]}
                        rows={characterisationRows}
                    />
                </Panel>

                <Panel span={12} dense title={t("views.pharmaSubgroupsOverview.shareOfCohortTitle")}>
                    <StackedBar items={shareItems} />
                </Panel>
            </DashboardGrid>
        </ViewContainer>
    );
}

export default SubgroupsOverviewView;
